
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import UniversalStatusBadge from '@/components/shared/UniversalStatusBadge';
import { FileText, Plus, Eye } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';

interface Invoice {
  id: string;
  invoice_number: string;
  title: string;
  status: string;
  created_at: string;
  due_date?: string;
  total_amount?: number;
  paid_amount?: number;
}

interface CustomerInvoicesProps {
  customerId: string;
}

const CustomerInvoices: React.FC<CustomerInvoicesProps> = ({ customerId }) => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInvoices();
  }, [customerId]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          id,
          invoice_number,
          title,
          status,
          created_at,
          due_date,
          total_amount,
          paid_amount
        `)
        .eq('customer_id', customerId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching invoices:', error);
        toast.error('Failed to load invoices');
        return;
      }

      setInvoices(data || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const renderStatusBadge = (invoice: Invoice) => (
    <UniversalStatusBadge 
      entityType="invoice" 
      status={invoice.status} 
      dateContext={invoice.due_date}
      size="sm" 
      showTooltip={false} 
    />
  );

  const navigate = useNavigate();

  const handleViewInvoice = (invoiceId: string) => {
    navigate(`/invoices/${invoiceId}`);
  };

  const handleCreateInvoice = () => {
    navigate(`/invoices/create?customer=${customerId}`);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Invoices
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <p>Loading invoices...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center">
          <FileText className="w-5 h-5 mr-2" />
          Invoices ({invoices.length})
        </CardTitle>
        <Button onClick={handleCreateInvoice} size="sm">
          <Plus className="w-4 h-4 mr-2" />
          New Invoice
        </Button>
      </CardHeader>
      <CardContent>
        {invoices.length > 0 ? (
          <div className="space-y-3">
            {invoices.map((invoice) => (
              <div
                key={invoice.id}
                className="p-4 border rounded-lg hover:bg-accent/50 transition-colors cursor-pointer"
                onClick={() => handleViewInvoice(invoice.id)}
              >
                <div className="flex items-start justify-between gap-2 min-w-0">
                  <div className="min-w-0 flex-1 space-y-1.5">
                    {/* Row 1: Invoice number + status */}
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium truncate">{invoice.invoice_number || 'Draft'}</span>
                      {renderStatusBadge(invoice)}
                    </div>
                    {/* Row 2: Title */}
                    <p className="text-sm text-muted-foreground truncate">{invoice.title}</p>
                    {/* Row 3: Dates */}
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>Created: {new Date(invoice.created_at).toLocaleDateString()}</span>
                      {invoice.due_date && (
                        <span>Due: {new Date(invoice.due_date).toLocaleDateString()}</span>
                      )}
                    </div>
                    {/* Row 4: Amounts - always on own line, never wrapped */}
                    {(invoice.total_amount !== undefined || invoice.paid_amount > 0) && (
                      <div className="flex items-center gap-3 text-xs">
                        {invoice.total_amount !== undefined && (
                          <span className="font-semibold text-foreground font-mono whitespace-nowrap">
                            {formatCurrency(invoice.total_amount)}
                          </span>
                        )}
                        {invoice.paid_amount > 0 && (
                          <span className="text-green-600 font-mono whitespace-nowrap">
                            Paid: {formatCurrency(invoice.paid_amount)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0"
                    onClick={(e) => { e.stopPropagation(); handleViewInvoice(invoice.id); }}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-2 opacity-20" />
            <p>No invoices found for this customer</p>
            <Button 
              onClick={handleCreateInvoice} 
              variant="outline" 
              className="mt-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create First Invoice
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomerInvoices;
